<script>
    import { navigate } from "svelte-routing";
    import { exportToXlsx, getUsers } from "../../../services/userService";
    import UserCard from "./UserCard.svelte";
    import Table from "../Table.svelte";
    import toast from "svelte-french-toast";
    import { format } from "date-fns";
    import Searchbar from "../../SearchBars/SearchBar.svelte";
    import { onDestroy, onMount } from "svelte";
    import { userTableFilter } from "../../../stores/userTableFilter";
    import { filterDefinedValues } from "../../../utils/filterDefinedValues";
    import { createColumnsFromQuery, stringifySortArray } from "../../../utils/sortFunction";
    import { downloadFile } from "../../../utils/downloadFile";
    import ButtonPromise from "../../Button/ButtonPromise.svelte";
    import Button from "../../Button/Button.svelte";
    import GroupSearcher from "../../SearchBars/GroupSearcher.svelte";
    import { getGroup } from "../../../services/groupService";

    let totalPages = 10;
    let users = [];
    let totalCount;
    let isLoading;
    let columns = [
        { displayName: "Nome", fieldName: "firstname", isSortable: true },
        { displayName: "Cognome", fieldName: "lastname", isSortable: true  },
        { displayName: "Email", fieldName: "email", isSortable: true, sortValue: "asc" },
        { displayName: "Ruolo", fieldName: "role" },
        { displayName: "Gruppo", fieldName: "group" },
        { displayName: "", fieldName: "" },
    ];
    let exportToXLSXPromise = null;
    let searchedGroup = null;

    async function getUserList(pagination) {
        isLoading = true;

        getUsers(pagination).then((resp) => {
            isLoading = false;

            if(resp.status === 200 && resp?.data) {
                users = resp.data.data;
                totalPages = Math.ceil(resp.data?.totalCount / $userTableFilter.size);
                totalCount = resp.data.totalCount;
            }

        }).catch((err) => {
            isLoading = false;

            if(err?.response?.status === 500) {
                toast.error(`Errore durante la ricerca, errore ${err.toString()}`);
            }
        })
    }

    async function handleExportToXLSX() {
		exportToXLSXPromise = (async () => {
			try {
				const data = await exportToXlsx();
				const fileName = `utenti_${format(new Date(), "dd_MM_yyyy")}.xlsx`;
				downloadFile(document, data.data, fileName);
				toast.success("Esportazione completata");
			} catch (err) {
                if(err?.response?.status === 500) {
                    toast.error(`Errore durante l'esportazione, errore ${err.toString()}`);
                }
			} finally {
				exportToXLSXPromise = null;
			}
		})();
	}

    const unsubscribeTableFilter = userTableFilter.subscribe((filter) => {
        if (!filter) {
            return;
        }

        const urlSearchParams = new URLSearchParams(filterDefinedValues(filter));
        navigate(!urlSearchParams.size ? "" : `?${urlSearchParams.toString()}`, { replace: false });

        getUserList(Object.fromEntries(urlSearchParams));
    });

    function updateFilterAndResetPage(key, value) {
        $userTableFilter = { ...$userTableFilter, page: 1, [key]: value }
    }

    function cleanFilters(options) {
        for (const key of options.map((o) => o.value)) {
            delete $userTableFilter[key];
        }

        $userTableFilter = { ...$userTableFilter };
    }

    function updateSortFilter(sorts) {
        $userTableFilter = { ...$userTableFilter, sort: stringifySortArray(sorts)};
    }

    onMount(async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const filters = Object.fromEntries(urlParams);

        if (!filters.page) {
            filters.page = "1"
        }
        if (!filters.size) {
            filters.size = "50"
        }

        if (!!filters.sort) {
            columns = createColumnsFromQuery(filters.sort, columns);
        }

        if (!!filters.groupId) {
            searchedGroup = (await getGroup(filters.groupId)).data;
        }

        $userTableFilter = filters;
    })

    onDestroy(() => {
        unsubscribeTableFilter();
    })

</script>

{#if !!$userTableFilter}
    <Table
        bind:page={$userTableFilter.page}
        bind:size={$userTableFilter.size}
        bind:totalPages
        bind:totalCount
        bind:isLoading
        bind:rowCount={users.length}
        bind:columns
        subject="Utenti"
        on:sort={(e) => updateSortFilter(e.detail)}
    >
        <div class="flex flex-col gap-2 md:flex-row justify-between w-full" slot="actions">
            <div class="relative w-full max-w-full md:w-44 font-semibold text-3xl flex-grow flex-1">
                Utenti
            </div>

            <div class="flex flex-col gap-1 lg:flex-row">
                <ButtonPromise
                    handleClick="{() => handleExportToXLSX()}"
                    promise="{exportToXLSXPromise}"
                    content="Esporta in XLSX"
                    buttonClass="w-full md:w-44 bg-blueGray-600"
                />

                <Button
                    handleClick="{() => navigate('create', {replace: true})}"
                    buttonClass="w-full md:w-44 bg-lightBlue-500 text-white"
                    content="Crea utente"
                />
            </div>
        </div>


        <div class="w-full flex flex-col gap-1 justify-between z-40" slot="searchbar">
            <Searchbar
                placeholder="Filtra per email del gruppo"
                value={$userTableFilter.email}
                on:search={(e) => updateFilterAndResetPage("email", e.detail)}
            />

            <div class="flex gap-1">
                <Searchbar
                    value={$userTableFilter.firstname}
                    hasLabel={false}
                    placeholder="Filtra per nome dell'utente"
                    on:search={(e) => updateFilterAndResetPage("firstname", e.detail)}
                />
                <Searchbar
                    value={$userTableFilter.lastname}
                    hasLabel={false}
                    placeholder="Filtra per cognome dell'utente"
                    on:search={(e) => updateFilterAndResetPage("lastname", e.detail)}
                />
            </div>

            <GroupSearcher
                isUserBased={true}
                isForm="{false}"
                value="{searchedGroup?.name}"
                on:change={(e) => updateFilterAndResetPage("groupId", e.detail.value)}
                on:clear={() => cleanFilters([ { value: "groupId" }])}
            />
        </div>

        <svelte:fragment slot="tableContent">
            {#each users as user}
                <UserCard {...user} on:delete={() => getUserList($userTableFilter)} />
            {/each}
        </svelte:fragment>
    </Table>
{/if}